import { useEffect, useState } from 'react';
import { usePreferredDealerListContent } from '../../services/preferred-dealer-ServiceXP/hooks/use-preferred-dealer-list-content';
import PreferredDealerService from '../../services/preferred-dealer/preferred-dealer';
import useUserProfile from '../../hooks/use-user-profile';
import ProfileService from '../../services/profile-service/profile-service';
import AuthenticationService from '../../services/authentication-service/authentication-service';
import { BreadcrumbWrapperReimagine } from '../../components/common/breadcrumb-wrapper-reimagine/breadcrumb-wrapper-reimagine';
import { useLocation } from 'react-router-dom';
import AddPreferredDealer from '../../components/sections/add-preferred-dealer/add-preferred-dealer';
import {
    GxpCustomUIParams,
    gxpCustomUIActionParams,
} from './gxp-customui-params';
import { GxpCustomUI } from '../../components/sections/gxp-customui/gxp-customui';
import './gxp-customui-view.scss';
import { ActivityIndicator } from '../../components/common/activity-indicator/activity-indicator';

export default function GxpCustomUIView() {
    const queryParams = new URLSearchParams(useLocation().search);
    const dealerCupid = queryParams.get('dealerCupid') ?? '';
    const [authenticatedState, setAuthenticatedUser] = useState('');
    const authenticationService = new AuthenticationService();
    const serviceCXContent = usePreferredDealerListContent();
    const profile = useUserProfile();
    const profileService = new ProfileService();
    const VIN = sessionStorage.getItem('selectedVin') || '';
    const APP_TEXT = 'R2-PEGA';
    const AUTH = 'loggedIn';
    const UN_AUTH = 'notLoggedIn';
    const [gxpUIParams, setGxpUIParams] = useState<GxpCustomUIParams>();
    const [hasCookie, setHasCookie] = useState(false);
    const [hasError, setHasError] = useState<boolean>(false);
    const [showLoader, setShowLoader] = useState<boolean>(false);
    const serviceCXBreadcrumb = serviceCXContent?.serviceCXBreadcrumb;
    const addPreferredDealerTitle = serviceCXContent?.addPreferredDealerTitle;
    const addPreferredDealerSubtitle =
        serviceCXContent?.addPreferredDealerSubtitle;
    const locateDealerCTALabel = serviceCXContent?.locateDealerCTALabel;
    const locateDealerCTAUrl = serviceCXContent?.locateDealerCTAUrl;
    const errorMessage = serviceCXContent?.errorMessage;
    const customUIInitUrl = process.env.REACT_APP_CUSTOM_UI_OW_INIT_URL;

    useEffect(() => {
        authenticationService
            .onIsAuthenticated()
            .then((isAuthenticated: boolean) => {
                if (isAuthenticated) {
                    setAuthenticatedUser(AUTH);
                } else {
                    setAuthenticatedUser(UN_AUTH);
                }
            });
    }, []);

    const getCookieWithSessionId = async (
        paCode: string,
        garageVins: string[],
        token: string
    ) => {
        setShowLoader(true);
        const url = `${customUIInitUrl}?PACode=${paCode}`;
        try {
            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    token,
                },
                body: JSON.stringify({ vinList: garageVins }),
                credentials: 'include',
            });

            if (response && response.status === 200) {
                setShowLoader(false);
                setHasError(false);
                setHasCookie(true);
                setGxpUIParams({
                    ...gxpCustomUIActionParams,
                    dealerCode: paCode,
                    isAuth: true,
                });
            } else {
                // Handle all other status codes
                console.error(`An error occurred: ${response.status}`);
                setHasError(true);
                setShowLoader(false);
                return;
            }
        } catch (error) {
            console.error('Error getting cookie with session id', error);
            setHasError(true);
            setShowLoader(false);
        }
    };

    const loadSwapToken = () => {
        return profileService
            .swapToken(APP_TEXT)
            .then(response => {
                return response?.access_token || '';
            })
            .catch(error => {
                console?.log('ServiceCX - Error retrieving data : ' + error);
                return '';
            });
    };

    const getPreferredDealer = () => {
        if (profile && profile?.vehicles?.length > 0) {
            const vehicleData = profile.vehicles.find(
                vehicle => vehicle.vin === VIN
            );
            if (vehicleData?.preferredDealer) {
                return vehicleData.preferredDealer;
            }
        }
        return '';
    };

    const passDealerCupIdAsPAcode = () => {
        // Passing dealer cupid as PACode if present in url
        if (dealerCupid) {
            setGxpUIParams({
                ...gxpCustomUIActionParams,
                dealerCode: dealerCupid,
            });
        } else {
            setGxpUIParams({
                ...gxpCustomUIActionParams,
            });
        }
    };

    const loadPACODE = () => {
        if (!VIN) {
            return '';
        }
        const preferredDealer = getPreferredDealer();
        if (!preferredDealer) {
            return '';
        }
        return new PreferredDealerService()
            .request(preferredDealer)

            .then(response => {
                if (
                    response?.dealerResultList &&
                    response?.dealerResultList.length > 0
                ) {
                    return response?.dealerResultList[0]?.smarttprovider === 'X'
                        ? response?.dealerResultList[0]?.unEncryptedDlrpa
                        : '';
                }
                return '';
            })
            .catch(error => {
                console?.log(
                    'ServiceCX - Error retrieving dealer code: ' +
                        preferredDealer +
                        '. Error: ' +
                        error
                );
                return '';
            });
    };

    const handleProfileAndGarageVins = () => {
        const garageVins: string[] = [];
        if (profile) {
            profile.vehicles?.forEach(vehicle => {
                vehicle.vin && garageVins.push(vehicle.vin);
            });
        }
        return garageVins;
    };

    const handleAuthFlow = async (garageVins: string[]) => {
        if (profile) {
            const [TOKEN, PACODE] = await Promise.all([
                loadSwapToken(),
                loadPACODE(),
            ]);
            let pa_code = PACODE ? getPreferredDealer() : dealerCupid;

            // if dealerCupid is present in URL, pick that always
            if (dealerCupid.trim().length !== 0) {
                pa_code = dealerCupid;
            }

            if (pa_code && TOKEN) {
                getCookieWithSessionId(pa_code, garageVins, TOKEN);
            } else {
                setGxpUIParams({ ...gxpCustomUIActionParams });
            }
        }
    };

    const handleUnAuthFlow = () => {
        passDealerCupIdAsPAcode();
    };

    useEffect(() => {
        const garageVins = handleProfileAndGarageVins();

        if (!gxpUIParams) {
            if (authenticatedState === AUTH) {
                handleAuthFlow(garageVins);
            } else if (authenticatedState === UN_AUTH) {
                handleUnAuthFlow();
            }
        }
    }, [authenticatedState, profile]);

    const showIframe = () => {
        if (authenticatedState === AUTH && gxpUIParams && hasCookie) {
            return <GxpCustomUI gxpUIParams={gxpUIParams} />;
        } else if (
            authenticatedState === UN_AUTH &&
            gxpUIParams &&
            gxpUIParams.dealerCode
        ) {
            return <GxpCustomUI gxpUIParams={gxpUIParams} />;
        } else if (
            (authenticatedState === UN_AUTH || authenticatedState === AUTH) &&
            gxpUIParams &&
            !gxpUIParams.dealerCode
        ) {
            return (
                <AddPreferredDealer
                    addPreferredDealerTitle={addPreferredDealerTitle}
                    addPreferredDealerSubtitle={addPreferredDealerSubtitle}
                    locateDealerCTALabel={locateDealerCTALabel}
                    locateDealerCTAUrl={locateDealerCTAUrl}
                />
            );
        }
    };

    return (
        <>
            {serviceCXBreadcrumb && (
                <BreadcrumbWrapperReimagine
                    hashId="support"
                    currentPage={serviceCXBreadcrumb}
                />
            )}
            {showLoader && <ActivityIndicator />}
            {hasError && <div className="cui-error">{errorMessage}</div>}
            {showIframe()}
        </>
    );
}
